import axios, { AxiosInstance } from "axios";
import { getOrCreateSessionId } from "./session";

const SourceHeaderName = "x-source";

/**
 * Creates an axios instance that automatically attaches telemetry headers to every request:
 * - `x-session-id`: stable per-tab session id (see {@link getOrCreateSessionId}).
 * - `x-trace-id`: a unique id per request.
 *
 * @returns A configured {@link AxiosInstance}.
 */
export function createHttpClient(): AxiosInstance {
    const client = axios.create();

    client.interceptors.request.use((config) => {
        try {
            config.headers["x-session-id"] = getOrCreateSessionId();
            config.headers["x-trace-id"] = crypto.randomUUID();
        } catch {
            // Telemetry headers are optional; never block a request if crypto.randomUUID()
            // is unavailable (e.g. insecure context or older browser).
        }
        return config;
    });

    return client;
}

/**
 * Adds the source scenario header to a request-specific headers object.
 *
 * Use this when the source is known only at the call site (for example, RAC main
 * vs Bing standalone forms, or Appeals account vs content submissions).
 *
 * @param headers - Existing request headers.
 * @param source - Optional scenario identifier sent as the `x-source` header.
 * @returns Headers with `x-source` included when a source is provided.
 */
export function withSourceHeader(headers: { [key: string]: string }, source?: string): { [key: string]: string } {
    return source ? { ...headers, [SourceHeaderName]: source } : headers;
}
